import { isDefined } from "~/shared/common";
import { WebhookEventKind } from "~/shared/enums";
import type { Webhook, WebhookUpdate } from "~/shared/types";
import { makeDuid } from "~/utils/common";
import { webhookComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, setObjectMapAndSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToWebhooks: Map<string, Webhook>;
  _sortedWebhookDuids: string[];
};

export const state: State = {
  _duidsToWebhooks: new Map(),
  _sortedWebhookDuids: [],
};

export type Getters = {
  webhookList: Webhook[];
  getWebhookByDuid: (duid: string) => Webhook | undefined;
};

export type Actions = {
  createWebhook: (
    url: string,
    order: string,
    partialWebhook?: Partial<Webhook>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Webhook | undefined>;
  updateWebhook: (update: WebhookUpdate, options?: { awaitBackend?: boolean }) => Promise<Webhook | undefined>;
  deleteWebhook: (webhook: Webhook, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createWebhookNoBackend: (url: string, order: string, partialWebhook?: Partial<Webhook>) => Webhook | undefined;
  $setWebhooks: (webhooks: Webhook[]) => void;
  $createOrUpdateWebhook: (webhook: Webhook) => void;
  $deleteWebhook: (webhook: Webhook) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  webhookList() {
    return this._sortedWebhookDuids.map((e) => this._duidsToWebhooks.get(e)).filter(isDefined);
  },
  getWebhookByDuid() {
    return (duid) => this._duidsToWebhooks.get(duid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createWebhook(url, order: string, partialWebhook = {}, options = {}) {
    const { awaitBackend = false } = options;
    const webhook = this.$createWebhookNoBackend(url, order, partialWebhook);
    if (!webhook) {
      return undefined;
    }

    const backendAction = this.$backend.webhook.create(webhook);
    if (awaitBackend) {
      await backendAction;
    }
    return webhook;
  },
  async updateWebhook(update, options = {}) {
    const { awaitBackend = false } = options;
    const webhook = this.getWebhookByDuid(update.duid);
    if (!webhook) {
      return undefined;
    }

    Object.assign(webhook, update);
    updateInSorted(this._duidsToWebhooks, this._sortedWebhookDuids, webhook.duid, webhookComparator);

    const backendAction = this.$backend.webhook.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return webhook;
  },
  async deleteWebhook(webhook, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteWebhook(webhook);

    const backendAction = this.$backend.webhook.delete(webhook.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $createWebhookNoBackend(url, order, partialWebhook = {}) {
    if (url === "") {
      return undefined;
    }

    const webhook: Webhook = {
      duid: makeDuid(),
      enabled: true,
      order,
      title: "",
      url: url.trim(),
      eventKinds: Object.values(WebhookEventKind),
      ...partialWebhook,
    };

    this.$createOrUpdateWebhook(webhook);

    return webhook;
  },
  $setWebhooks(webhooks) {
    setObjectMapAndSorted(this._duidsToWebhooks, this._sortedWebhookDuids, webhooks, webhookComparator);
  },
  $createOrUpdateWebhook(webhook) {
    const currentWebhook = this.getWebhookByDuid(webhook.duid);

    if (!currentWebhook) {
      this._duidsToWebhooks.set(webhook.duid, webhook);
    } else {
      Object.assign(currentWebhook, webhook);
    }
    updateInSorted(this._duidsToWebhooks, this._sortedWebhookDuids, webhook.duid, webhookComparator);
  },
  $deleteWebhook(webhook) {
    this._duidsToWebhooks.delete(webhook.duid);
    removeFromSorted(this._sortedWebhookDuids, webhook.duid);
  },
};

export { actions, getters };
